import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type UseFormReturn, useForm } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import { Company, WorkType, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import useApiError from 'hooks/useApiError';
import { selectors } from 'store';
import { changeRenamedWorkTypes } from 'utils';

export interface CompanyWorkTypesFormValues {
  work_types: WorkType[] | undefined;
  work_types__selected: string | undefined;
}

export interface CompanyWorkTypesFormHook {
  form: UseFormReturn<CompanyWorkTypesFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<CompanyWorkTypesFormValues>,
  ) => void;
  isSubmitting: boolean;
}

export const formatWorkTypeBody = (
  values: CompanyWorkTypesFormValues,
  company?: Company,
): Company | undefined => {
  if (!company) return undefined;
  const companyInfo = company?.company_info;
  if (!companyInfo) return;
  return {
    ...company,
    company_info: {
      ...companyInfo,
      work_types: values.work_types,
    },
  };
};

export default function useCompanyWorkTypesForm(): CompanyWorkTypesFormHook {
  const company = useSelector(selectors.company.getEntity);
  const isSubmitting = useSelector(selectors.company.getIsSubmitting);
  const dispatch = useAppDispatch();
  const onError = useApiError('Unknown error updating company work types');
  const { showSuccess } = useToast();

  const defaultValues = useMemo(() => {
    if (!company)
      return {
        work_types: undefined,
        work_types__selected: undefined,
      };
    const current = company?.company_info?.work_types;
    if (!current)
      return {
        work_types: undefined,
        work_types__selected: undefined,
      };
    const mapped = changeRenamedWorkTypes(current);
    return {
      work_types: mapped,
      work_types__selected: mapped.join(';'),
    };
  }, [company]);

  const form = useForm<CompanyWorkTypesFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues,
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<CompanyWorkTypesFormValues>) => {
      const id = company?.id;
      if (!id) return;
      const fv = formValues as unknown as CompanyWorkTypesFormValues;
      const body = formatWorkTypeBody(fv, company);
      if (!body) return;
      dispatch(
        actions.putCompany({
          params: { id },
          body,
          onError,
          onSuccess: () => {
            showSuccess('Work type added successfully');
          },
        }),
      );
    },
    [company, dispatch, onError, showSuccess],
  );

  useEffect(() => {
    if (!company) return;
    form.reset(defaultValues);
  }, [form, company, defaultValues]);

  return {
    form,
    onSubmit,
    isSubmitting,
  };
}
